import { takeEvery } from 'containers/ErrorBoundary';
import { closeDialogue } from 'containers/LeaveDialogue/actions';
import get from 'lodash/get';
import { FETCH_ADVANCED } from 'pages/Settings//DiscoverySnapshots/GlobalDiscoveryConfiguration/constants';
import { BACKUP_FORM_NAME } from 'pages/Settings/System/BackupMaintenance/Backup/constants';
import { MAINTENANCE_FORM_NAME } from 'pages/Settings/System/BackupMaintenance/Maintenance/constants';
import { changeSnapshot } from 'pages/SnapshotManagement/actions';
import { push } from 'redux-first-history';
import { reset, setSubmitFailed, startSubmit, stopSubmit } from 'redux-form';
import { all, call, put, select } from 'redux-saga/effects';
import { requestForSaga } from 'utils/request/request';
import { requestCustomErrorMessage } from 'utils/request/requestCustomErrorMessage';
import { requestMessage } from 'utils/request/requestMessage';
import {
  SAVE_BACKUP_FORM,
  SAVE_MAINTENANCE,
  SETTING_SYSTEM_CLEAR_DB,
  START_MAINTENANCE,
} from './constants';

function* clearDb(action: any): Generator<any, void, any> {
  try {
    yield put({ type: `${SETTING_SYSTEM_CLEAR_DB}_START` });
    yield call(requestForSaga, {
      method: 'POST',
      url: '/os/clean-db',
      data: action.payload,
    });
    yield put({ type: `${SETTING_SYSTEM_CLEAR_DB}_SUCCESS` });
    yield put(changeSnapshot(null));
  } catch (e) {
    yield put({ type: `${SETTING_SYSTEM_CLEAR_DB}_ERROR` });
    yield call(requestCustomErrorMessage, 'danger', e);
    throw e;
  }
}

function* saveBackupForm(action: any): Generator<any, void, any> {
  try {
    const {
      data: { enabled },
      returnUrl,
    } = action.payload;
    yield put({ type: `${SAVE_BACKUP_FORM}_START` });
    const isEnabled = !!enabled;
    yield put(startSubmit(BACKUP_FORM_NAME));
    // don't send empty password to keep current one when patching on backend
    const { password = null, ...data } = isEnabled ? action.payload.data : {};
    if (password !== '' && password !== null) {
      data.password = password;
    }
    const payload = yield call(requestForSaga, {
      url: '/settings',
      method: 'PATCH',
      data: { backup: { ...data, enabled: isEnabled } },
    });
    yield put({
      type: `${FETCH_ADVANCED}_SUCCESS`,
      payload,
    });
    yield call(requestMessage, 'success', 'savedAdvancedSystemBackup');
    yield put(reset(BACKUP_FORM_NAME));
    if (returnUrl) {
      yield put(
        push(
          { pathname: returnUrl.pathname, search: returnUrl.search },
          returnUrl.state,
        ),
      );
    }
    yield put(stopSubmit(BACKUP_FORM_NAME));
    yield put({ type: `${SAVE_BACKUP_FORM}_SUCCESS` });
  } catch (e) {
    const leaveDialogueOpen = yield select((s) =>
      s.getIn(['leaveDialogue', 'open']),
    );
    if (leaveDialogueOpen) {
      yield put(closeDialogue());
    }
    yield put(setSubmitFailed(BACKUP_FORM_NAME));
    yield put(stopSubmit(BACKUP_FORM_NAME));
    yield put({ type: `${SAVE_BACKUP_FORM}_ERROR` });
    if (get(e, 'code') === 'API_INVALID_DESTINATION') {
      const type = get(action, 'payload.data.type');
      if (type === 'local') {
        yield call(requestMessage, 'warning', 'backupInvalidDestination');
      } else {
        yield call(
          requestMessage,
          'warning',
          'backupInvalidFTPDestination',
          (t) => t.replace('%protocol%', type.toUpperCase()),
        );
      }
    } else {
      yield call(requestCustomErrorMessage, 'danger', e);
      throw e;
    }
  }
}

function* saveMaintenanceForm(action: any): Generator<any, void, any> {
  try {
    const { values, returnUrl } = action.payload;
    yield put(startSubmit(MAINTENANCE_FORM_NAME));
    const enabled = values.get('enabled');
    yield call(requestForSaga, {
      url: '/settings',
      method: 'PATCH',
      data: {
        maintenance: {
          enabled,
          ...(enabled && { cron: values.get('cron') }),
        },
      },
    });
    yield put({
      type: `${SAVE_MAINTENANCE}_SUCCESS`,
      payload: action.payload,
    });
    yield call(requestMessage, 'success', 'savedAdvancedSystemMaintenance');
    yield put(reset(MAINTENANCE_FORM_NAME));
    if (returnUrl) {
      yield put(
        push(
          { pathname: returnUrl.pathname, search: returnUrl.search },
          returnUrl.state,
        ),
      );
    }
    yield put(stopSubmit(MAINTENANCE_FORM_NAME));
  } catch (e) {
    const leaveDialogueOpen = yield select((s) =>
      s.getIn(['leaveDialogue', 'open']),
    );
    if (leaveDialogueOpen) {
      yield put(closeDialogue());
    }
    yield put(setSubmitFailed(MAINTENANCE_FORM_NAME));
    yield put(stopSubmit(MAINTENANCE_FORM_NAME));
    yield call(requestCustomErrorMessage, 'danger', e);
    throw e;
  }
}

export function* startMaintenanceSaga(): Generator<any, void, any> {
  try {
    yield call(requestForSaga, {
      url: '/os/maintenance',
      method: 'POST',
    });
  } catch (e) {
    yield call(requestCustomErrorMessage, 'danger', e);
    throw e;
  }
}

export function* defaultSaga(): Generator<any, void, any> {
  yield all([
    takeEvery(SAVE_BACKUP_FORM, saveBackupForm),
    takeEvery(SAVE_MAINTENANCE, saveMaintenanceForm),
    takeEvery(START_MAINTENANCE, startMaintenanceSaga),
    takeEvery(SETTING_SYSTEM_CLEAR_DB, clearDb),
  ]);
}

export default defaultSaga;
